import React, { FC, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from 'src/components/common/Button';
import { CSLoader } from 'src/components/common/CsLoader';
import { Typography } from 'src/components/common/Typography';
import { icons } from 'src/configs/icons';
import { getCanonicalUrl, SeoHelmet, toSeoMeta } from 'src/helpers/seo';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { CmsPage } from 'src/types/cms/cmsCommonTypes';
import { CmsPromotionPageFields } from 'src/types/cms/cmsPageTypes';
import './blogInfoPage.scss';

type Props = {
  handleBack: () => void;
  blogPageInfo: CmsPage<CmsPromotionPageFields>;
  handleRedirect: () => void;
};

const BlogInfoPage: FC<Props> = ({ handleBack, handleRedirect, blogPageInfo }) => {
  const { t }: Translation = useTranslation();
  const { pathname } = useLocation();
  const [iframeHeight, setIframeHeight] = useState<any>(0);
  const [isLoading, setIsLoading] = useState(true);
  const sessionRecorder = useSessionRecorder();
  const meta = toSeoMeta(blogPageInfo?.content?.meta);

  const banner = useMemo(() => {
    if (isMobile && !!blogPageInfo?.content?.pageFields.bannerMobile) {
      return blogPageInfo.content.pageFields.bannerMobile;
    }

    if (!isMobile && !!blogPageInfo?.content?.pageFields.bannerDesktop) {
      return blogPageInfo.content.pageFields.bannerDesktop;
    }

    return null;
  }, [blogPageInfo]);

  const handleIframeLoad = (): void => {
    setIsLoading(false);
  };

  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (event.data.type === 'iframeHeight') {
        setIframeHeight(event.data.height);
      }
    });
  }, []);

  useEffect(() => {
    if (blogPageInfo?.content?.pageFields?.cardTitle) {
      sessionRecorder?.trackEvent('Blog page view', {
        blogName: blogPageInfo.content.pageFields.cardTitle,
      });
    }
  }, [blogPageInfo]);

  return (
    <div className="blogs-info-page">
      <SeoHelmet
        category={blogPageInfo?.content?.pageFields?.cardTitle || 'blog'}
        meta={meta}
        canonicalUrl={getCanonicalUrl(pathname)}
      />
      <Button
        onClick={handleBack}
        className="blogs-info-page__back-button-wrapper"
        icon={icons.controlPrev}
        justifyContentValue="flex-start"
      >
        <span>{t('backToBlog')}</span>
      </Button>
      <div className="blogs-info-page__description-wrapper">
        {!!banner && !blogPageInfo?.content?.pageFields?.externalContentUrl && (
          <img className="blogs-info-page__description-img" src={banner.path} alt={t('descriptionImage')} />
        )}
        <div className="blogs-info-page__description-title-text-button-wrapper">
          {blogPageInfo?.content?.pageFields?.externalContentUrl ? (
            <>
              {isLoading && (
                <div className="loader-container">
                  <CSLoader speed="slow" />
                </div>
              )}
              <iframe
                onLoad={handleIframeLoad} // Handle iframe load
                style={{ height: iframeHeight || 'initial', display: isLoading ? 'none' : 'block' }} // Hide iframe while loading
                src={blogPageInfo?.content?.pageFields?.externalContentUrl}
                className="blog_frame"
              ></iframe>
            </>
          ) : (
            <>
              <Typography
                variant={'h6'}
                children={
                  <span
                    className="promotions-info-page__description-text"
                    dangerouslySetInnerHTML={{ __html: blogPageInfo?.content?.pageFields.title || '' }}
                  />
                }
              />

              <Typography
                variant={'h6'}
                children={
                  <span
                    className="blogs-info-page__description-text"
                    dangerouslySetInnerHTML={{ __html: blogPageInfo.content?.pageFields?.description || '' }}
                  />
                }
              />
            </>
          )}
          {blogPageInfo?.content?.pageFields.cta?.variant && (
            <div className="blog-btn">
              <Button onClick={handleRedirect} className="blogs__button blogs__button--description success-filled-btn">
                {t(blogPageInfo?.content?.pageFields.cta.variant)}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogInfoPage;
